import { assortMasterServiceItems } from '@/helpers/serviceCategory';
import { useNextRouter } from '@/hooks/useNextRouter';
import {
  api_portalOfficeListing_filter,
  service_RequestFilter,
} from '@/init/swagger';
import { SearchStationDetailType } from '@/schemas/SearchStationDetailSchema';
import { pathInfo } from '@/types/pathInfo';
import { LISTING_FILTER_CATEGORY_TYPE } from '@/types/types';
import { useForm, useFormContext } from 'react-hook-form';

export type PublicSearchFilterType =
  | 'serviceAndAdditionStatus'
  | 'requireLogin'
  | null;

type Args = {
  close: () => void;
  selectedSearchFilters: service_RequestFilter[];
  searchFilters: api_portalOfficeListing_filter[];
  searchFilterType: PublicSearchFilterType;
};

export const useLogic = ({
  close,
  selectedSearchFilters,
  searchFilters,
  searchFilterType,
}: Args) => {
  const router = useNextRouter();
  const { setValue } = useFormContext<SearchStationDetailType>();
  const filterFormMethod = useForm<{ [name: string]: boolean }>({
    defaultValues: {
      ...selectedSearchFilters.reduce(
        (acc, filter) => {
          return {
            ...acc,
            [`${filter.category_id}_${filter.search_filter_id}`]: true,
          };
        },
        {} as { [name: string]: boolean },
      ),
    },
  });

  const filters = assortMasterServiceItems({ masterItems: searchFilters });

  const reduceInitialValue: {
    [key in string]: (typeof filters)['additionStatuses'];
  } = {};
  const additionStatuses = filters.additionStatuses.reduce((result, value) => {
    if (!value || !value.label) return result;

    const categoryName = value.label.replace('【', '').split('】')[0];
    const label = value.label.replace('【', '').split('】')[1];
    if (!result[categoryName]) {
      result[categoryName] = [];
    }
    result[categoryName].push({ ...value, label });
    return result;
  }, reduceInitialValue);

  const handleFilter = () => {
    const selected: api_portalOfficeListing_filter[] = [];

    Object.entries(filterFormMethod.getValues()).forEach(([key, value]) => {
      if (!value) return;

      const categoryId = parseInt(key.split('_')[0]);
      const filterId = parseInt(key.split('_')[1]);
      const searchFilter = searchFilters.find((sf) => {
        return sf.category_id == categoryId && sf.id == filterId;
      });

      if (!searchFilter) return;

      selected.push(searchFilter);
    });

    setValue(
      'filters',
      selected.map(({ id, category_id }) => ({
        category_id,
        search_filter_id: id,
      })),
    );
    setValue('is_no_addition', filterFormMethod.getValues('isNoAddition'));

    const newQuery = new URLSearchParams(window.location.search);
    newQuery.delete(
      pathInfo['/public/stations/search'].searchParams.searchFilters,
    );
    newQuery.delete(pathInfo['/public/stations/search'].searchParams.page);
    setValue('page', 1);

    selected.forEach((filter) => {
      newQuery.append(
        pathInfo['/public/stations/search'].searchParams.searchFilters,
        `${filter.category_id}_${filter.id}`,
      );
    });

    newQuery.delete(
      pathInfo['/public/stations/search'].searchParams.isNoAddition,
    );
    if (filterFormMethod.getValues('isNoAddition')) {
      newQuery.append(
        pathInfo['/public/stations/search'].searchParams.isNoAddition,
        'true',
      );
    }

    router.push(`/public/stations/search?${newQuery.toString()}`);

    close();
  };

  const handleClose = () => {
    close();
  };

  const handleReset = () => {
    const targetSearchFilters = Object.entries(
      filterFormMethod.getValues(),
    ).filter(([key, value]) => {
      if (!value) return false;

      const categoryId = parseInt(key.split('_')[0]);
      if (searchFilterType === 'serviceAndAdditionStatus') {
        return (
          categoryId == LISTING_FILTER_CATEGORY_TYPE.SERVICE ||
          categoryId == LISTING_FILTER_CATEGORY_TYPE.ADDITION_STATUS ||
          categoryId == LISTING_FILTER_CATEGORY_TYPE.OTHERS
        );
      }
    });

    targetSearchFilters.forEach(([key, _]) => {
      filterFormMethod.setValue(key, false);
    });
    if (searchFilterType === 'serviceAndAdditionStatus')
      filterFormMethod.setValue('isNoAddition', false);
  };

  return {
    filterFormMethod,
    additionStatuses,
    handleFilter,
    handleClose,
    handleReset,
    filters,
  };
};
